/**
 * My account — the page behind the person's name: who they are (by name,
 * never by the opaque uid), their clinic, their clinic role by NAME, language,
 * password, sign out (design 1C). The clinic role is the personnel profile's
 * job title (Clinic domain, cms-backend) — readable behind
 * clinic.personnel.read; without it the row says so honestly rather than
 * inventing a role from permission keys or borrowing Auth's RBAC names. Auth
 * access roles are deliberately NOT shown here. Sign out lives in the shell's
 * top bar, not in this card.
 */

import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import s from './access.module.css';
import { Button, Chip, Segmented } from '@/ui/kit';
import { clinicRoleOf, useMyIdentity, useMyPersonnel } from '@/core/auth/useMyIdentity';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useLocale, type Locale } from '@/core/i18n/LocaleProvider';
import { fetchClinic, clinicDisplayName } from '@/core/clinic/api';
import { EntryUrl } from '@/features/entry/EntryUrl';
import { qk } from '@/core/api/keys';

function RowIcon({ d }: { d: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {d.split('|').map((p) => (
        <path key={p} d={p} />
      ))}
    </svg>
  );
}

/**
 * The clinic-role row, one honest state at a time. The job title is the role
 * line the product already shows on the calendar and rosters; the specialty
 * rides beside it when the profile carries one.
 */
function RoleValue({
  personnel,
  t,
}: {
  personnel: ReturnType<typeof useMyPersonnel>;
  t: (id: string) => React.ReactNode;
}) {
  if (personnel.status === 'loading') return <>…</>;
  if (personnel.status === 'unreadable') return <>{t('account.roleUnavailable')}</>;
  if (personnel.status === 'error') {
    return (
      <>
        {t('account.roleFailed')}{' '}
        <Button variant="link" onClick={personnel.retry}>
          {t('rec.retry')}
        </Button>
      </>
    );
  }
  const role = clinicRoleOf(personnel.view);
  const specialty = personnel.view?.practitioner?.specialty?.trim() || null;
  if (role === null && specialty === null) return <>{t('account.roleNone')}</>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {role && (
        <Chip kind="info">
          <span dir="auto">{role}</span>
        </Chip>
      )}
      {specialty && (
        <Chip kind="quiet">
          <span dir="auto">{specialty}</span>
        </Chip>
      )}
    </span>
  );
}

export function AccountPage() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const authz = useAuthz();
  const { locale, setLocale } = useLocale();

  const clinicQuery = useQuery({
    queryKey: authz.clinicId ? qk.clinic(authz.clinicId) : ['clinic', 'none'],
    queryFn: () => fetchClinic(),
    enabled: authz.clinicId !== null,
    staleTime: 10 * 60_000,
  });

  const identity = useMyIdentity();
  const personnel = useMyPersonnel();
  const memberships = authz.state.status === 'ready' ? authz.state.snapshot.memberships : [];

  return (
    <div>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>{t('account.title')}</h1>
        <p className={s.pageSub}>{t('account.sub')}</p>
      </div>

      <div className={s.sectionCard}>
        <div className={s.row}>
          <span className={s.rowIcon}>
            <RowIcon d="M12 11a3.4 3.4 0 1 0 0-6.8A3.4 3.4 0 0 0 12 11z|M5 19.5a7 7 0 0 1 14 0" />
          </span>
          <span className={s.rowKey}>{t('account.fullName')}</span>
          <span className={s.rowValue} dir="auto">
            {identity.name ?? (identity.loading ? '…' : t('account.noName'))}
          </span>
        </div>

        <div className={s.row}>
          <span className={s.rowIcon}>
            <RowIcon d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8-4.3-4.1 5.9-.9z" />
          </span>
          <span className={s.rowKey}>{t('account.role')}</span>
          <span className={s.rowValue}>
            <RoleValue personnel={personnel} t={t} />
            <span className={s.rowHint}>{t('account.roleHint')}</span>
          </span>
        </div>

        <div className={s.row}>
          <span className={s.rowIcon}>
            <RowIcon d="M4 20V9.5L12 4l8 5.5V20|M9.5 20v-6h5v6" />
          </span>
          <span className={s.rowKey}>{t('account.clinic')}</span>
          <span className={s.rowValue}>
            {clinicQuery.data ? clinicDisplayName(clinicQuery.data) : '…'}
            {/* ZOY-614: the address this clinic hands its staff — from the
                snapshot's slug, so a clinic without one shows nothing. */}
            {(() => {
              const slug = memberships.find((m) => m.clinicId === authz.clinicId)?.slug ?? null;
              return slug ? (
                <span className={s.rowHint}>
                  <EntryUrl slug={slug} />
                </span>
              ) : null;
            })()}
          </span>
          {memberships.length > 1 && (
            <span className={s.rowAction}>
              <Button
                variant="quiet"
                onClick={() => {
                  authz.clearSelection();
                }}
              >
                {t('account.switchClinic')}
              </Button>
            </span>
          )}
        </div>

        <div className={s.row}>
          <span className={s.rowIcon}>
            <RowIcon d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0|M3 12h18|M12 3c2.5 2.4 3.8 5.6 3.8 9s-1.3 6.6-3.8 9c-2.5-2.4-3.8-5.6-3.8-9s1.3-6.6 3.8-9z" />
          </span>
          <span className={s.rowKey}>{t('account.language')}</span>
          <span className={s.rowValue}>
            <Segmented<Locale>
              label={t('account.language')}
              options={[
                { value: 'fa', label: 'فارسی' },
                { value: 'en', label: 'English' },
              ]}
              value={locale}
              onChange={setLocale}
            />
          </span>
        </div>

        <div className={s.row}>
          <span className={s.rowIcon}>
            <RowIcon d="M4 10.5h16v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19.5z|M8 10.5V7a4 4 0 0 1 8 0v3.5" />
          </span>
          <span className={s.rowKey}>{t('account.password')}</span>
          <span className={s.rowValue}>
            <Link to="/account/change-password">{t('account.passwordValue')}</Link>
          </span>
        </div>
      </div>
    </div>
  );
}
